// Hover-to-copy for the values people lift out of a page.
//
// Two things can be offered a copy button, whatever the content around them:
//
//   * a whole value: inline code, a code block, a detail row, a reference chip,
//     or a table or grid cell. A cell or row is a value only when its text is one
//     short line, holds no link label (a title or a status filter), and holds no
//     value inside a sentence. In a spreadsheet grid marked data-hover-copy-cells,
//     every cell is a value;
//   * a token inside any text: a reference, amount, date, email, URL, UUID or
//     phone number under the pointer, found by what it looks like. This is what
//     covers headings, event messages, notes, AI text, and link text that cannot
//     be selected without opening the link. A sentence copies its values one by
//     one, never as a whole.
//
// One delegated listener and one floating button, so nothing is added to the
// rendered markup and Turbo-streamed content works without wiring. The value
// that a click copies is marked by a dotted outline with no fill, and the button
// sits inside the outline's end. Where there is no room for it there (a
// value in running text, a cell that fills its column), it perches on the
// outline's corner instead. Shift-click on an amount copies the plain number (no
// currency sign or thousands separators) for pasting into another system.
//
// Where it does not apply: the extracted-data form (.xd-form) and task facts
// (each has its own copy control), menus and nav, form fields, and Tabulator headers. Opt a
// region out with data-hover-copy="off". Opt an element in with data-hover-copy
// (a short value, say a reference in a header), and set data-hover-copy-text
// when the text to copy differs from the text shown.

const SHOW_DELAY_MS = 120
// A value inside a link: hovering it first means "what is this?" (the link's
// hover card) or "open it", so the copy pill waits until the pointer stays.
const LINK_SHOW_DELAY_MS = 700
const HIDE_DELAY_MS = 140
const MOVE_THROTTLE_MS = 50
const COPIED_MS = 1200
// The dotted outline stands FRAME_PAD_* off the value's text. The button sits
// WELL_GAP from the text and WELL_INSET from the outline's border.
const FRAME_BORDER = 2 // the outline's border width in hover_copy.css
const FRAME_PAD_X = 4
const FRAME_PAD_TIGHT = 1
const FRAME_PAD_Y = 1
const WELL_GAP = 4
const WELL_INSET = 2
const FRAME_GAP = 3 // how far outside a code block its outline stands
// The button's click area reaches this far past it (`.hover-copy::before` in
// hover_copy.css). It must stay inside the cell holding the value, or a click in
// it would copy this value from the next cell's text.
const HIT_AREA = 6

const EXCLUDED = [
  '[data-hover-copy="off"]', '.data-sheet', '.task-context', '.xd-form', '.hover-copy',
  // A ui_value carries its own copy button (UiHelper), so it gets no second one.
  '.ui-value', '.ui-copy',
  '.tabulator-header', '.tabulator-footer', '.tabulator-row-header', '.tabulator-col',
  'nav', '.menu', '[role="menu"]', '[role="listbox"]', '[role="tooltip"]',
  'summary', 'textarea', 'input', 'select', 'script', 'style', '[contenteditable="true"]'
].join(', ')
// Detail rows and reference chips: values that are not a table cell or code.
const VALUE = '[data-hover-copy]:not([data-hover-copy="off"]), .data-list-value, .key-details__value, .paper-ref-chip, dd'
const CELL = 'td, .tabulator-cell'
const SKIP_CONTENT = 'input, textarea, select, button, [contenteditable="true"]'
const SKIP_CODE = '.event-state, :has(> a[data-ref-type])'
// The most characters a whole cell or row copies. Longer text is prose: its values copy one by one.
const MAX_VALUE_LENGTH = 80

// ---- What a value looks like ------------------------------------------------

const CURRENCY = '[$€£¥]'
const PATTERNS = [
  ['email', /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/],
  ['url', /^https?:\/\/\S+$/i],
  ['uuid', /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i],
  ['date', /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/],
  ['date', /^\d{1,2}[/.]\d{1,2}[/.]\d{2,4}$/],
  ['phone', /^\+?\(?\d{3}\)?[-. ]\d{3}[-.]\d{4}$/],
  // An amount: a currency sign, thousands grouping, or exactly two decimals.
  ['money', new RegExp(`^\\(?[-+]?${CURRENCY}\\s?[-+]?\\d[\\d,]*(?:\\.\\d+)?\\)?$`)],
  ['money', /^\(?[-+]?\d{1,3}(?:,\d{3})+(?:\.\d+)?\)?%?$/],
  ['money', /^[-+]?\d+\.\d{2}%?$/],
  ['number', /^\d{5,}$/],
  // Numeric references joined by a separator: 77-20418, 2026/0188.
  ['id', /^\d{2,}[-/]\d{3,}(?:[-/]\d+)*$/],
  // A reference or part number: letters and digits together, long enough or
  // joined by a separator, so words and small numbers are left alone.
  ['id', /^#?(?=[A-Za-z0-9._:/#-]*\d)(?=[A-Za-z0-9._:/#-]*[A-Za-z])[A-Za-z0-9][A-Za-z0-9._:/#-]*[A-Za-z0-9]$/]
]

export function valueKind (text) {
  if (!text || text.length > 200) return null
  const hit = PATTERNS.find(([kind, pattern]) => pattern.test(text))
  if (!hit) return null
  if (hit[0] === 'id' && text.length < 5 && !/[-_/#]/.test(text)) return null
  return hit[0]
}

// The value-like token that holds the character at `offset` in `text`, with its
// bounds, or null. Words, sentences, and small numbers are not values.
export function tokenAround (text, offset) {
  let at = Math.min(offset, text.length - 1)
  if (at >= 0 && /\s/.test(text[at]) && offset > 0) at = offset - 1
  if (at < 0 || /\s/.test(text[at])) return null

  let start = at
  let end = at + 1
  while (start > 0 && !/\s/.test(text[start - 1])) start--
  while (end < text.length && !/\s/.test(text[end])) end++

  let token = text.slice(start, end)
  const lead = token.match(/^["'“‘<[]+/)?.[0].length || 0
  const trail = token.slice(lead).match(/[.,;:!?"'”’>\]]+$/)?.[0].length || 0
  start += lead
  end -= trail
  token = text.slice(start, end)
  if (token.endsWith(')') && !token.includes('(')) { token = token.slice(0, -1); end -= 1 }
  if (token.startsWith('(') && !token.includes(')')) { token = token.slice(1); start += 1 }
  // "(ap@vendor.example.com)" is a value in parentheses; "(1,204.50)" is an amount.
  if (!valueKind(token) && token.startsWith('(') && token.endsWith(')')) { token = token.slice(1, -1); start += 1; end -= 1 }

  return valueKind(token) ? { start, end, text: token } : null
}

// "$1,204.50" or "(1,204.50)" as 1204.50 / -1204.50, for pasting into a system
// that wants a bare number. Null when the text is not an amount or is already plain.
export function plainNumber (text) {
  const match = text.trim().match(new RegExp(`^(\\()?([-+])?${CURRENCY}?\\s?([-+])?(\\d[\\d,]*(?:\\.\\d+)?)%?\\)?$`))
  if (!match) return null
  const negative = Boolean(match[1]) || match[2] === '-' || match[3] === '-'
  const plain = `${negative ? '-' : ''}${match[4].replace(/,/g, '')}`
  return plain === text.trim() ? null : plain
}

// ---- What is under the pointer ----------------------------------------------

// The structured value under `node`, or null. A code block wins over anything
// inside it; inline code wins over the value or cell around it, so hovering a
// part number copies the part number, not the whole cell.
export function copyTargetFor (node) {
  const element = node?.nodeType === 3 ? node.parentElement : node
  if (!element?.closest || element.closest(EXCLUDED)) return null

  const block = element.closest('pre')
  if (block) return copyTextFor(block) ? block : null

  const code = element.closest('code')
  if (code && !code.matches(SKIP_CODE) && !code.closest('a, button, label') && copyTextFor(code)) return code

  const value = element.closest(VALUE)
  if (value && usable(value, { allowLink: value.hasAttribute('data-hover-copy') })) return value

  const cell = element.closest(CELL)
  if (cell && usable(cell, { allowLink: true })) return cell

  return null
}

// A link is a way to navigate, so a value that is itself a link is left alone
// unless a view opted it in. A cell holding a link copies only when the link text
// is itself a value (a reference), not a title or a status filter.
function usable (element, { allowLink }) {
  if (element.querySelector(SKIP_CONTENT) || element.closest('button')) return false
  if (!allowLink && element.closest('a')) return false
  const text = copyTextFor(element)
  if (!text) return false
  return element.hasAttribute('data-hover-copy') || isWholeValue(element, text)
}

// A whole element is a value when it holds no link label and, unless it sits in a
// spreadsheet grid (where every cell is the value), is one short line with no value
// inside a sentence. An opt-in is the author's call, so it skips these checks.
function isWholeValue (element, text) {
  if ([...element.querySelectorAll('a[href]')].some(link => !valueKind(link.textContent.trim()))) return false
  if (element.closest('[data-hover-copy-cells]')) return true
  if (text.includes('\n') || text.length > MAX_VALUE_LENGTH) return false
  return valueKind(text) !== null || valueTokensIn(text).length === 0
}

// The value-like tokens in a text, in order. A plain sentence has none.
export function valueTokensIn (text) {
  return [...text.matchAll(/\S+/g)].map(match => tokenAround(text, match.index)).filter(Boolean)
}

export function copyTextFor (element) {
  if (element.dataset?.hoverCopyText) return element.dataset.hoverCopyText
  const text = element.matches('pre, code') ? element.textContent : element.innerText
  const trimmed = (text || '').replace(/\s+$/, '')
  return element.matches('pre') ? trimmed : trimmed.replace(/^\s+/, '')
}

// True when other content sits on the value's line in the space the button
// would take: the words after (or, for a number aligned right, before) it in the
// same parent. Such a value is in running text, so its button perches instead.
function crowdedBeside (element, text, reach, atStart, doc) {
  const parent = element.parentNode
  if (!parent) return false
  const range = doc.createRange()
  if (atStart) {
    range.setStart(parent, 0)
    range.setEndBefore(element)
  } else {
    range.setStartAfter(element)
    range.setEnd(parent, parent.childNodes.length)
  }
  const slot = atStart ? { left: text.left - reach, right: text.left } : { left: text.right, right: text.right + reach }
  return [...range.getClientRects()].some(rect =>
    rect.width > 1 && rect.left < slot.right && rect.right > slot.left &&
    rect.top < text.bottom - 2 && rect.bottom > text.top + 2)
}

// The text node and offset under a point, in whichever form the browser offers.
function caretAt (doc, x, y) {
  if (doc.caretPositionFromPoint) {
    const position = doc.caretPositionFromPoint(x, y)
    return position ? { node: position.offsetNode, offset: position.offset } : null
  }
  if (doc.caretRangeFromPoint) {
    const range = doc.caretRangeFromPoint(x, y)
    return range ? { node: range.startContainer, offset: range.startOffset } : null
  }
  return null
}

// A value-like token under the pointer, as a Range, or null. The caret APIs
// answer with the nearest character even when the pointer is in empty space, so
// the pointer must actually be over the token's box.
function tokenTargetAt (doc, x, y, element) {
  const caret = caretAt(doc, x, y)
  const node = caret?.node
  if (node?.nodeType !== 3 || !node.parentElement || node.parentElement.closest(EXCLUDED)) return null
  // A reference already carries its own highlight and its card's copy button,
  // so the copy pill stays off the words inside it.
  if (node.parentElement.closest('pre, code, button, label, a[data-ref-type]')) return null

  const token = tokenAround(node.data, caret.offset)
  if (!token) return null

  const range = doc.createRange()
  range.setStart(node, token.start)
  range.setEnd(node, token.end)
  const over = [...range.getClientRects()].some(r => x >= r.left - 2 && x <= r.right + 2 && y >= r.top - 2 && y <= r.bottom + 2)
  return over ? { range, text: token.text, element: node.parentElement, kind: 'token' } : null
}

// What to offer at a point: the whole value it sits in, else the token under it.
// A value is never a sentence, so the target does not change kind as the pointer
// moves along one cell.
function candidateAt (doc, x, y) {
  const element = doc.elementFromPoint(x, y)
  if (!element || element.closest('.hover-copy')) return null

  const structured = copyTargetFor(element)
  if (structured) {
    return { element: structured, text: copyTextFor(structured), kind: structured.matches('pre') ? 'block' : 'value' }
  }
  return tokenTargetAt(doc, x, y, element)
}

// Where the pill lives: in the open dialog that holds the value, else the body.
// A modal dialog sits in the browser's top layer, above every z-index in the
// body, so a pill in the body would hide behind the viewer while its highlight
// still showed.
// How long a target waits before its pill shows. Moving between values while
// the pill is up swaps at once; a fresh hover waits a beat so sweeping across a
// table does not flicker; a value inside a link always waits longer.
export function showDelayFor (next, showing) {
  if (next?.element?.closest?.('a[href]')) return LINK_SHOW_DELAY_MS
  return showing ? 0 : SHOW_DELAY_MS
}

export function pillHostFor (element, doc) {
  return element?.closest?.('dialog[open]') || doc.body
}

function sameCandidate (a, b) {
  if (!a || !b) return false
  if (a.kind === 'token' || b.kind === 'token') {
    return a.kind === b.kind && a.range.startContainer === b.range.startContainer &&
      a.range.startOffset === b.range.startOffset && a.range.endOffset === b.range.endOffset
  }
  return a.element === b.element
}

// How far from the text's edge the outline's outer edge lies once it holds the button.
export function wellReach (width) {
  return WELL_GAP + width + WELL_INSET + FRAME_BORDER
}

// Where the outline and the button go, from plain rectangles (so it can be tested
// without a DOM). `text` is the value's own text, `first` its first line, and
// `cellBox` the cell holding it, if any. Returns the outline's rectangle, the
// button's left and top, and whether the button perches on the outline's corner.
// The button and its click area stay inside the cell and the viewport.
export function layoutFor ({ kind, box, text, first, cellBox, alignedEnd, crowded, width, size, viewportWidth }) {
  if (kind === 'block') {
    // The block is its own box: ring it and tuck the button into its corner.
    return {
      outline: { left: box.left - FRAME_GAP, top: box.top - FRAME_GAP, right: box.right + FRAME_GAP, bottom: box.bottom + FRAME_GAP },
      left: box.right - width - 8,
      top: box.top + 8,
      perched: false
    }
  }

  const floor = Math.max(4, cellBox?.left ?? 0)
  const ceiling = Math.min(viewportWidth - 4, cellBox?.right ?? Infinity)
  // Room beside the value for the button, its click area included.
  const need = Math.max(wellReach(width), WELL_GAP + width + HIT_AREA)
  const room = alignedEnd ? text.left - need >= floor : text.right + need <= ceiling
  const slotted = kind !== 'token' && room && !crowded

  // Hugging the words (perched) keeps the dots off their neighbours; an outline
  // that holds the button is given a little air.
  const around = FRAME_BORDER + (slotted ? FRAME_PAD_X : FRAME_PAD_TIGHT)
  const outline = { left: text.left - around, right: text.right + around, top: text.top - FRAME_BORDER - FRAME_PAD_Y, bottom: text.bottom + FRAME_BORDER + FRAME_PAD_Y }

  if (!slotted) {
    // Straddling the outline's top-right corner, pulled in until the click area is inside the cell.
    return {
      outline,
      left: Math.max(floor + HIT_AREA, Math.min(outline.right - width + 6, ceiling - HIT_AREA - width)),
      top: Math.max(4, outline.top - size / 2),
      perched: true
    }
  }

  const top = first.top + (first.height - size) / 2
  let left
  if (alignedEnd) {
    left = text.left - WELL_GAP - width
    outline.left = left - WELL_INSET - FRAME_BORDER
  } else {
    left = text.right + WELL_GAP
    outline.right = left + width + WELL_INSET + FRAME_BORDER
  }
  // The outline grows to hold the button when a short line is shorter than it.
  outline.top = Math.min(outline.top, top - WELL_INSET - FRAME_BORDER)
  outline.bottom = Math.max(outline.bottom, top + size + WELL_INSET + FRAME_BORDER)
  return { outline, left, top, perched: false }
}

// The surface `element` sits on: the first background behind it that is mostly
// opaque, and whether it is dark, so the outline and the button can be drawn to
// show against it. The browser reports a colour in whatever space it was written
// in (rgb, oklch, color-mix), so a one-pixel canvas turns it into plain RGB.
function surfaceOf (element, win) {
  const canvas = win.document.createElement('canvas')
  canvas.width = canvas.height = 1
  const context = canvas.getContext('2d', { willReadFrequently: true })
  for (let el = element; el; el = el.parentElement) {
    const css = win.getComputedStyle(el).backgroundColor
    if (!css || css === 'transparent') continue
    context.clearRect(0, 0, 1, 1)
    context.fillStyle = css
    context.fillRect(0, 0, 1, 1)
    const [r, g, b, a] = context.getImageData(0, 0, 1, 1).data
    if (a < 128) continue
    return { dark: (0.299 * r + 0.587 * g + 0.114 * b) < 110, color: `rgb(${r} ${g} ${b})` }
  }
  return { dark: false, color: null }
}

// The async clipboard API needs a secure context and a granted permission; a
// self-hosted install served over plain http has neither, so fall back to the
// selection-based copy the browser still allows inside a click.
export async function writeClipboard (doc, text) {
  const win = doc.defaultView
  if (win.navigator.clipboard?.writeText) {
    try {
      await win.navigator.clipboard.writeText(text)
      return
    } catch (error) {
      // fall through to the selection-based copy
    }
  }

  const field = doc.createElement('textarea')
  field.value = text
  field.setAttribute('readonly', '')
  field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none'
  doc.body.appendChild(field)
  field.select()
  try {
    if (!doc.execCommand('copy')) throw new Error('copy command refused')
  } finally {
    field.remove()
  }
}

export function attachHoverCopy (doc = document) {
  if (doc.defaultView?.__hoverCopyAttached) return
  const win = doc.defaultView || window
  if (!win.matchMedia?.('(hover: hover)').matches) return
  win.__hoverCopyAttached = true

  let button = null
  let frame = null
  let target = null
  let pending = null
  let showTimer = null
  let hideTimer = null
  let resetTimer = null
  let moveTimer = null
  let lastMove = null

  const build = () => {
    const el = doc.createElement('button')
    el.type = 'button'
    el.className = 'hover-copy'
    el.setAttribute('aria-label', 'Copy value')
    el.innerHTML = '<i class="ti ti-copy" aria-hidden="true"></i><span class="hover-copy__label">Copied</span>'
    el.addEventListener('mouseenter', () => clearTimeout(hideTimer))
    el.addEventListener('mouseleave', scheduleHide)
    el.addEventListener('mousedown', event => event.preventDefault())
    el.addEventListener('click', copy)
    return el
  }

  // The dotted outline around the value. It never takes the pointer, so the value
  // under it stays hoverable.
  const buildFrame = () => {
    const el = doc.createElement('div')
    el.className = 'hover-copy-frame'
    el.setAttribute('aria-hidden', 'true')
    return el
  }

  const hide = () => {
    clearTimeout(showTimer)
    clearTimeout(hideTimer)
    target = null
    pending = null
    button?.classList.remove('hover-copy--visible')
    frame?.classList.remove('hover-copy-frame--visible')
  }

  const scheduleHide = () => {
    clearTimeout(showTimer)
    clearTimeout(hideTimer)
    pending = null
    hideTimer = setTimeout(hide, HIDE_DELAY_MS)
  }

  const show = next => {
    clearTimeout(hideTimer)
    target = next
    pending = null
    // Turbo redraws the viewer on every switch, which can take a button that lived
    // in its dialog with it.
    if (!button?.isConnected) button = build()
    if (!frame?.isConnected) frame = buildFrame()
    const host = pillHostFor(target.element, doc)
    for (const piece of [frame, button]) if (piece.parentElement !== host) host.appendChild(piece)
    reset()
    const plain = plainNumber(target.text)
    button.title = plain ? `Copy · Shift-click copies ${plain}` : 'Copy'
    // The button sits on the value's own surface; a code block's outline stands outside it.
    const inside = surfaceOf(target.element, win)
    const outside = target.kind === 'block' ? surfaceOf(target.element.parentElement, win) : inside
    button.classList.toggle('hover-copy--on-dark', inside.dark)
    frame.classList.toggle('hover-copy-frame--on-dark', outside.dark)
    if (inside.color) button.style.setProperty('--hover-copy-surface', inside.color)
    else button.style.removeProperty('--hover-copy-surface')
    place()
    button.classList.add('hover-copy--visible')
    frame.classList.add('hover-copy-frame--visible')
  }

  const reset = () => {
    clearTimeout(resetTimer)
    button.classList.remove('hover-copy--copied')
    frame.classList.remove('hover-copy-frame--copied')
    button.querySelector('i').className = 'ti ti-copy'
    button.setAttribute('aria-label', 'Copy value')
  }

  // Draws the outline around the target and seats the button (see layoutFor).
  // Values with room get the button inside the outline's end (its start, for a
  // number aligned right). A token in running text has no room beside it, and
  // neither does a cell that fills its column, so there the button perches on the
  // outline's top corner instead of covering the words next to it.
  const place = () => {
    const width = button.offsetWidth || 18
    const size = button.offsetHeight || 18
    const box = target.element.getBoundingClientRect()
    const input = { kind: target.kind, box, width, size, viewportWidth: win.innerWidth }

    if (target.kind !== 'block') {
      const isToken = target.kind === 'token'
      // Measure the text itself, not the box: a cell is wider than its value.
      const range = isToken ? target.range : doc.createRange()
      if (!isToken) range.selectNodeContents(target.element)
      const lines = range.getClientRects()
      const first = lines[0] || box
      const last = lines[lines.length - 1] || box
      const all = range.getBoundingClientRect()
      const content = all.width || all.height ? all : box
      const style = win.getComputedStyle(target.element)
      const cell = target.element.closest(CELL)
      input.cellBox = cell?.getBoundingClientRect()
      input.alignedEnd = !isToken && ['right', 'end'].includes(style.textAlign)
      input.first = first

      // A token's outline follows its last line; a value's wraps every line. A value
      // cut off with an ellipsis is wider than what shows: the outline stops at the
      // edge you can see, inside the padding when the value is the cell itself.
      if (isToken) {
        input.text = { left: last.left, right: last.right, top: last.top, bottom: last.bottom }
      } else {
        const padded = target.element.matches(CELL)
        input.text = {
          left: Math.max(content.left, box.left + (padded ? parseFloat(style.paddingLeft) || 0 : 0)),
          right: Math.min(content.right, box.right - (padded ? parseFloat(style.paddingRight) || 0 : 0)),
          top: content.top,
          bottom: content.bottom
        }
        // A cell is bounded by its own box, which layoutFor checks; its neighbours are
        // other cells, whose clipped text can still report a width past them.
        input.crowded = !padded && crowdedBeside(target.element, input.text, wellReach(width), input.alignedEnd, doc)
      }
    }

    const { outline, left, top, perched } = layoutFor(input)
    button.style.left = `${Math.max(4, left)}px`
    button.style.top = `${Math.max(4, top)}px`
    button.classList.toggle('hover-copy--perched', perched)
    Object.assign(frame.style, {
      left: `${outline.left}px`,
      top: `${outline.top}px`,
      width: `${outline.right - outline.left}px`,
      height: `${outline.bottom - outline.top}px`
    })
  }

  async function copy (event) {
    event.preventDefault()
    event.stopPropagation()
    if (!target) return

    const shown = target.text
    const text = event.shiftKey ? (plainNumber(shown) || shown) : shown

    try {
      await writeClipboard(doc, text)
      button.classList.add('hover-copy--copied')
      frame.classList.add('hover-copy-frame--copied')
      button.querySelector('i').className = 'ti ti-check'
      button.setAttribute('aria-label', 'Copied')
      place() // the label widens the pill
      clearTimeout(resetTimer)
      resetTimer = setTimeout(() => { reset(); place() }, COPIED_MS)
    } catch (error) {
      console.error('Copy failed:', error)
      button.setAttribute('aria-label', 'Copy failed')
    }
  }

  const settle = () => {
    moveTimer = null
    const { x, y } = lastMove
    const next = candidateAt(doc, x, y)

    if (!next) {
      clearTimeout(showTimer)
      pending = null
      if (target) scheduleHide()
      return
    }
    if (sameCandidate(next, target)) {
      clearTimeout(hideTimer)
      return
    }
    if (sameCandidate(next, pending)) return

    clearTimeout(showTimer)
    pending = next
    showTimer = setTimeout(() => show(next), showDelayFor(next, Boolean(target)))
  }

  // Throttled: the lookup runs at most every MOVE_THROTTLE_MS, on the latest position.
  doc.addEventListener('mousemove', event => {
    if (event.buttons) return // dragging to select text: stay out of its way
    if (button && button.contains(event.target)) return
    lastMove = { x: event.clientX, y: event.clientY }
    moveTimer ||= setTimeout(settle, MOVE_THROTTLE_MS)
  }, { passive: true })

  doc.addEventListener('mousedown', event => {
    if (!button || !button.contains(event.target)) hide()
  })
  doc.documentElement.addEventListener('mouseleave', () => { if (target) scheduleHide() })
  doc.addEventListener('scroll', hide, true)
  doc.addEventListener('turbo:before-cache', hide)
  doc.addEventListener('turbo:before-render', hide)
}
